const HerbalDrugTechUnit4Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-green-900 mb-3 border-b-2 border-slate-200 pb-2">Unit IV: Evaluation, Patenting & Regulatory Issues</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit focuses on the quality control of herbal drugs, the protection of traditional knowledge through Intellectual Property Rights (IPR), and the regulatory framework governing Ayurvedic, Siddha, and Unani (ASU) drugs in India.
        </p>
    </div>

    <!-- 2. Evaluation of Drugs -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">1. Evaluation & Stability Testing of Herbal Drugs</h3>
        
        <div class="mb-5">
            <h4 class="text-[16px] font-bold text-green-900 mb-2">A. WHO & ICH Guidelines for Assessment</h4>
            <p class="text-[13px] text-slate-700 mb-2">The evaluation of herbal drugs ensures their <strong>Quality, Safety, and Efficacy</strong>. WHO guidelines emphasize:</p>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600">
                <li><strong>Botanical Evaluation:</strong> Macroscopic and microscopic identification to prevent adulteration.</li>
                <li><strong>Physicochemical Parameters:</strong> Ash values (total, acid-insoluble), extractive values, and moisture content (loss on drying).</li>
                <li><strong>Biological & Toxicological Limits:</strong> Testing for heavy metals (Lead, Cadmium, Arsenic), pesticide residues, microbial contamination (E. coli, Salmonella), and radioactive contamination.</li>
                <li><strong>Chromatographic Profiling:</strong> TLC, HPLC, and HPTLC fingerprinting to identify active marker compounds.</li>
            </ul>
        </div>

        <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
            <h4 class="text-[15px] font-bold text-green-800 mb-2">B. Stability Testing of Herbal Drugs</h4>
            <p class="text-[13px] text-slate-700 mb-2">Stability testing determines the shelf-life of the product under specific environmental conditions (Temperature, Humidity, Light).</p>
            <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
                <li><strong>Challenges:</strong> Herbal drugs are complex mixtures. Stability is usually determined by monitoring specific "marker compounds" over time.</li>
                <li><strong>Real-Time Testing:</strong> Done at normal storage conditions for a longer period.</li>
                <li><strong>Accelerated Testing:</strong> Done under exaggerated conditions (e.g., 40°C ± 2°C and 75% RH) for 6 months to predict shelf-life rapidly.</li>
            </ul>
        </div>
    </div>

    <!-- 3. Patenting and Regulatory Requirements -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">2. Patenting & IPR of Natural Products</h3>
        
        <!-- Definitions -->
        <div class="overflow-x-auto rounded-lg border border-slate-200 mb-6">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-green-50 text-green-900">
                    <tr>
                        <th class="px-4 py-3 font-bold w-1/4">Term</th>
                        <th class="px-4 py-3 font-bold">Definition & Significance</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-700">
                    <tr>
                        <td class="px-4 py-3 font-bold">IPR (Intellectual Property Rights)</td>
                        <td class="px-4 py-3">Legal rights granted to creators/inventors for their intellectual creations, preventing others from using it without permission. Includes Patents, Trademarks, Copyrights, etc.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Patent</td>
                        <td class="px-4 py-3">An exclusive right granted by the government for an invention that is <strong>novel, non-obvious, and has industrial application</strong>, usually for 20 years.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Farmers' Right</td>
                        <td class="px-4 py-3">Rights of farmers to save, use, sow, replant, exchange, share, or sell their farm produce, including seeds of a protected variety.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Breeder's Right</td>
                        <td class="px-4 py-3">Rights granted to breeders of a new, distinct, uniform, and stable plant variety to control its production and marketing.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Bioprospecting</td>
                        <td class="px-4 py-3">The systematic search for and development of new sources of chemical compounds, genes, organisms, and other valuable products from nature.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-red-700">Biopiracy</td>
                        <td class="px-4 py-3">The unethical and illegal appropriation of indigenous traditional knowledge or biological resources by commercial entities without fair compensation or permission.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Case Studies -->
        <div>
            <h4 class="text-[16px] font-bold text-green-900 mb-3">Traditional Knowledge (TK) & Famous Case Studies</h4>
            <p class="text-[13px] text-slate-700 mb-3">Traditional Knowledge cannot be patented because it lacks "novelty" (it is already known/prior art). India created the <strong>TKDL (Traditional Knowledge Digital Library)</strong> to prevent the biopiracy of Indian herbs.</p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="border border-red-200 p-4 rounded-lg bg-red-50">
                    <h5 class="font-bold text-red-800 text-[14px] mb-2">Case Study 1: Curcuma (Turmeric)</h5>
                    <p class="text-[12px] text-slate-700">
                        <strong>The Issue:</strong> In 1995, the US Patent Office granted a patent to the University of Mississippi for the use of Turmeric (Haldi) in wound healing.<br><br>
                        <strong>The Resolution:</strong> India's CSIR challenged the patent, providing ancient Ayurvedic texts as evidence that turmeric has been used for wound healing in India for centuries. The patent was successfully <strong>revoked</strong> for lack of novelty.
                    </p>
                </div>
                <div class="border border-red-200 p-4 rounded-lg bg-red-50">
                    <h5 class="font-bold text-red-800 text-[14px] mb-2">Case Study 2: Neem</h5>
                    <p class="text-[12px] text-slate-700">
                        <strong>The Issue:</strong> A US company (W.R. Grace) and the US Dept of Agriculture were granted a European patent for a fungicidal product derived from Neem seeds.<br><br>
                        <strong>The Resolution:</strong> The Indian government and NGOs challenged it, proving that the fungicidal properties of Neem were "prior art" and public knowledge in India. The European Patent Office <strong>revoked</strong> the patent in 2000.
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- 4. Regulatory Issues (India) -->
    <div class="bg-indigo-50 p-6 border border-indigo-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-900 mb-4 border-b border-indigo-200 pb-2">3. Regulatory Issues & Regulations in India</h3>
        <p class="text-[13px] text-slate-700 mb-3">In India, herbal drugs are regulated under the <strong>Drugs and Cosmetics Act, 1940</strong>, specifically under the provisions for Ayurveda, Siddha, and Unani (ASU) systems.</p>

        <ul class="list-disc pl-5 space-y-3 text-[13px] text-slate-700 mb-4">
            <li>
                <strong>ASU DTAB (Ayurveda, Siddha, Unani Drugs Technical Advisory Board):</strong><br>
                A statutory board that advises the Central and State Governments on technical matters relating to the manufacture and control of ASU drugs.
            </li>
            <li>
                <strong>ASU DCC (Drugs Consultative Committee):</strong><br>
                An advisory committee that ensures uniformity throughout India in the administration of the Drugs and Cosmetics Act regarding ASU drugs.
            </li>
            <li>
                <strong>Schedule Z (Regulation of Manufacture of ASU Drugs):</strong><br>
                Schedule Z outlines the regulatory guidelines, requirements, and provisions specifically meant for the manufacture and sale of approved Ayurvedic, Siddha, and Unani drugs to ensure quality and standard control.
            </li>
        </ul>
    </div>

</div>
`;

export default HerbalDrugTechUnit4Notes;